import { useQueryClient } from '@tanstack/react-query';
import { Spin } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { OfficerIdentityDto } from '@pams/shared';
import { clearArrangementListFilters } from '@/hooks/useArrangementListFilters';
import type { AuthLostReason } from '@/lib/api';
import { api, clearTokens, getRefreshToken, refreshAccessToken, setAuthFailureHandler, setTokens } from '@/lib/api';
import { AuthContext, type AuthContextValue, type AuthStatus } from '@/lib/authContext';
import { queryKeys } from '@/lib/queryKeys';

/**
 * Session state, and the bootstrap that runs before any protected route renders.
 *
 * ── Why rendering is GATED during bootstrap ─────────────────────────────────────
 *
 * The single-flight guard in `lib/api.ts` would already collapse N simultaneous 401s
 * into one refresh, so letting pages mount and recover would work. It would also mean
 * every page fires a doomed request and flashes an error state first. One round trip
 * behind a spinner removes the whole class of problem, and it means a reload on
 * `/login` with a live session lands you inside rather than showing the form for 200ms.
 */
/**
 * ⚠️ [amended C1a] WHAT A PERSON IS TOLD WHEN THEIR SESSION ENDS.
 *
 * Until this pass they were told nothing at all: the app swapped to the login form with
 * no sentence, no toast and nothing in the URL. On the surface where a revocation is
 * SUPPOSED to happen — a stolen token detected, an account deactivated, a demotion — that
 * is indistinguishable from a bug, which is how C1a §1 was experienced.
 *
 * ⚠️ IT DISCLOSES NOTHING NEW, AND THAT IS WHY IT IS SAFE. The reader is looking at a
 * login screen, so they already know they are signed out; "your session ended" is exactly
 * what reaching the end of the session would have told them. It deliberately does NOT claim detection —
 * no "suspicious activity", no "replay", no "revoked for security" — because the server's
 * 401 is uniform ACROSS expired, revoked and replayed and must stay that way
 * (`api/src/services/token.service.ts`). The client cannot tell them apart and must not
 * pretend to.
 */
const SIGNED_OUT_COPY: Record<Exclude<AuthLostReason['kind'], 'forbidden'>, string> = {
  'session-ended': 'Your session ended, so you were signed out. Sign in again to continue.',
  'signed-out-elsewhere': 'You were signed out in another tab or window.',
};

/**
 * ⚠️ THE 403 CARRIES THE SERVER'S OWN SENTENCE, VERBATIM. It is the one safe disclosure
 * here, on the argument `ERROR_CODES.WRONG_SURFACE` already makes: it describes the
 * CALLER'S OWN authorisation, which they hold, and names no role, scope, record or
 * person. Re-wording it here would be a second copy of a string the API owns and
 * `authTokens.test.ts` pins.
 */
const sentenceFor = (reason: AuthLostReason): string =>
  reason.kind === 'forbidden' ? reason.message : SIGNED_OUT_COPY[reason.kind];

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<AuthStatus>('bootstrapping');
  const [officer, setOfficer] = useState<OfficerIdentityDto | undefined>();
  const [signedOutReason, setSignedOutReason] = useState<string | undefined>();
  const queryClient = useQueryClient();
  // React 19 StrictMode double-mounts effects in development. Without this the bootstrap
  // runs twice, and the second refresh presents a token the first has already rotated —
  // which the server correctly reads as reuse and punishes by killing the family.
  const bootstrapped = useRef(false);

  const reset = useCallback(
    (reason?: string) => {
      clearTokens();
      /**
       * ⚠️ [LIST-FILTERS] AND THE LIST FILTER GOES WITH THE SESSION — the user's rule is "until
       * sign-out", and this is every way a session ends here: a sign-out, a session lost
       * elsewhere (the auth-lost handler below), a failed `/auth/me`. It sweeps every officer's
       * key, on `queryClient.clear()`'s argument further down: per-officer UI state must not
       * outlive the session on a shared browser. Every other tab follows through the `storage`
       * event, so a sign-out in one tab unfilters them all.
       */
      clearArrangementListFilters();
      setOfficer(undefined);
      setStatus('anonymous');
      // Absent for a deliberate sign-out, and for a bootstrap that simply found no session:
      // neither is something to explain, and a stale sentence on the login form is worse
      // than none.
      setSignedOutReason(reason);
      /**
       * Not housekeeping. Leaving one nodal's officer rows in the cache for whoever signs
       * in next on the same browser is a data-leak-shaped bug, and the scoping work on the
       * server does not help once the data is already on the client.
       */
      queryClient.clear();
    },
    [queryClient],
  );

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    const run = async () => {
      if (!getRefreshToken()) {
        setStatus('anonymous');
        return;
      }
      const token = await refreshAccessToken();
      if (!token) {
        /*
         * ⚠️ [LIST-FILTERS] NO `clearArrangementListFilters()` HERE, DELIBERATELY — nor in the
         * no-refresh-token branch above. EVERY failed refresh ends here, but only one of them is
         * this branch's to judge. On a server refusal, or a refresh token another context removed
         * while this one waited, `apiClient.ts` has ALREADY called the auth-lost handler — `reset`,
         * which cleared — before this line runs. The one failure that arrives here with no `reset`
         * is a NETWORK failure: the client keeps the tokens on `status === 0`, so the session may be
         * alive in every other tab, and a clear here would wipe that live session's filter in all of
         * them through the `storage` event. So it must not clear, and it must not be read as
         * "offline" either. `AuthProvider.test.tsx` A4 is the negative control for the network case.
         */
        setStatus('anonymous');
        return;
      }
      try {
        const me = await api.get<OfficerIdentityDto>('/auth/me');
        queryClient.setQueryData(queryKeys.auth.me(), me);
        setOfficer(me);
        setStatus('authenticated');
      } catch {
        reset();
      }
    };

    void run();
  }, [queryClient, reset]);

  // How `lib/` tells React the session is gone, without importing React.
  useEffect(() => {
    setAuthFailureHandler((reason) => reset(sentenceFor(reason)));
    return () => setAuthFailureHandler(undefined);
  }, [reset]);

  const signIn = useCallback<AuthContextValue['signIn']>(
    (session) => {
      /*
       * ⚠️ [LIST-FILTERS] A NEW SESSION STARTS CLEAN. This is also the only thing that enforces
       * "until sign-out" when no `reset` was ever observed — a session that simply lapsed, then an
       * OTP sign-in — and a sign-in here moves every other tab onto the new session's refresh
       * family, so clearing their filter too is correct rather than collateral.
       */
      clearArrangementListFilters();
      setTokens({ accessToken: session.accessToken, refreshToken: session.refreshToken });
      setSignedOutReason(undefined);
      queryClient.setQueryData(queryKeys.auth.me(), session.officer);
      setOfficer(session.officer);
      setStatus('authenticated');
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    const refreshToken = getRefreshToken();
    try {
      await api.post('/auth/logout', { ...(refreshToken ? { refreshToken } : {}), allSessions: false });
    } catch {
      // The local teardown runs regardless. A network failure must never leave someone
      // apparently signed in on a machine they are walking away from.
    }
    reset();
  }, [reset]);

  const clearSignedOutReason = useCallback(() => {
    setSignedOutReason(undefined);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, officer, signIn, signOut, signedOutReason, clearSignedOutReason }),
    [status, officer, signIn, signOut, signedOutReason, clearSignedOutReason],
  );

  if (status === 'bootstrapping') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background" data-testid="auth-bootstrapping">
        <Spin size="large" tip="Restoring your session…">
          <div className="h-24 w-24" />
        </Spin>
      </div>
    );
  }

  return <AuthContext value={value}>{children}</AuthContext>;
};
